/* ==========================================================================
   Sleep Karma — Cart Drawer Smart Upsell
   Click-to-promote flow:
     1. user clicks card → instant "✓ ADDED" green state
     2. brief hold so the user sees the confirmation
     3. POST /cart/add.js with sections=cart-drawer,cart-icon-bubble
     4. fade out the upsell strip, then innerHTML-swap the section
     5. new line item slides in via the existing sk-slide-in keyframe;
        the upsell snippet rerenders with the next progressive offer
   ========================================================================== */
(() => {
  'use strict';

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const ADD_FEEDBACK_MS = 700;
  const FADE_OUT_MS = 260;

  let isAdding = false;

  function bindCards(root) {
    const scope = root || document;
    scope.querySelectorAll('.sk-xs__card:not([data-bound])').forEach((card) => {
      card.dataset.bound = '1';
      card.addEventListener('click', onCardClick);
    });
  }

  async function onCardClick(event) {
    event.preventDefault();
    if (isAdding) return;

    const card = event.currentTarget;
    const variantId = card.dataset.variantId;
    if (!variantId) return;

    isAdding = true;

    const btn = card.querySelector('.sk-xs__btn');
    card.classList.add('is-added');
    if (btn) btn.textContent = '✓ ADDED';

    await sleep(ADD_FEEDBACK_MS);

    try {
      const data = await addToCart(variantId);

      const xs = card.closest('.sk-xs');
      if (xs) {
        xs.classList.add('is-fading-out');
        await sleep(FADE_OUT_MS);
      }

      applySections(data.sections);
      document.dispatchEvent(new CustomEvent('sk:cart-line-added', { detail: data }));
      bindCards();
      notifyCartUpdate();
    } catch (err) {
      console.error('[cart-upsell] add failed', err);
      card.classList.remove('is-added');
      if (btn && btn.dataset.originalLabel) {
        btn.textContent = btn.dataset.originalLabel;
      }
      const xs = card.closest('.sk-xs');
      if (xs) xs.classList.remove('is-fading-out');
    } finally {
      isAdding = false;
    }
  }

  async function addToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: SECTIONS,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error('cart-add ' + res.status + ': ' + text);
    }
    return res.json();
  }

  function applySections(sections) {
    if (!sections) return;

    if (sections['cart-drawer']) {
      const newDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const newDrawer = newDoc.querySelector('#CartDrawer');
      const oldDrawer = document.querySelector('#CartDrawer');
      if (newDrawer && oldDrawer) {
        oldDrawer.innerHTML = newDrawer.innerHTML;
      }
    }

    if (sections['cart-icon-bubble']) {
      const newDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newBubble = newDoc.querySelector('#cart-icon-bubble');
      const oldBubble = document.querySelector('#cart-icon-bubble');
      if (newBubble && oldBubble) {
        oldBubble.innerHTML = newBubble.innerHTML;
      }
    }
  }

  function notifyCartUpdate() {
    if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      try {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell' });
      } catch (e) {
        // theme pubsub not available — fine, the section swap is enough
      }
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-upsell' } }));
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function init() {
    bindCards();

    // Re-bind whenever any code (including ours) swaps cart-drawer DOM.
    // Idempotent: bindCards skips already-bound cards.
    const observerTarget = document.querySelector('cart-drawer') || document.body;
    const observer = new MutationObserver(() => bindCards());
    observer.observe(observerTarget, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
